import { Component, OnDestroy, OnInit, inject, input, output, effect } from '@angular/core';
import { Artist, Composer, ContractType, InvoiceStatus } from '../../../models/db-models';
import { ArtistService } from '../../services/artist.service';
import { DbService } from '../../services/db.service';
import { Payee } from '../../../models/external-system.model';
import { PayeeService } from '../../services/payee.service';
import { NgClass, AsyncPipe, CurrencyPipe } from '@angular/common';
import { IonGrid, IonRow, IonCol, IonIcon, IonLabel, IonChip, IonButton, IonNote, IonAccordionGroup, IonAccordion, IonItem } from '@ionic/angular/standalone';
import { addIcons } from "ionicons";
import { personCircle, chevronDown, checkmarkCircleOutline, timeOutline, closeCircleOutline, returnDownForwardOutline, banOutline } from "ionicons/icons";
import { Observable, of } from 'rxjs';

@Component({
  selector: 'app-artist-card',
  templateUrl: './artist-card.component.html',
  styleUrls: ['./artist-card.component.scss'],
  imports: [
    NgClass,
    AsyncPipe,
    CurrencyPipe,
    IonGrid,
    IonRow,
    IonCol,
    IonIcon,
    IonLabel,
    IonChip,
    IonButton,
    IonNote,
    IonAccordionGroup,
    IonAccordion,
    IonItem,
  ],
  standalone: true,
})
export class ArtistCardComponent implements OnInit, OnDestroy {
  private db = inject(DbService);
  artistService = inject(ArtistService);
  payeeService = inject(PayeeService);

  readonly selected = input<boolean>(undefined);
  readonly artistInput = input<Artist | null>(null, { alias: 'artist' });
  artist: Artist;
  readonly payeeInput = input<Payee | null>(null, { alias: 'payee' });
  payee: Payee;
  readonly composerInput = input<Composer | null>(null, { alias: 'composer' });
  composer: Composer;
  readonly self = input<boolean>(undefined);
  readonly version = input('full');
  readonly showChangeArtist = input(false);
  readonly isChildPayeeDisplay = input(false);
  readonly changeClick = output();
  readonly linkedPayeeSelected = output<string>();
  artistStatus: InvoiceStatus = null;
  autoExpandLinkedPayees = false;
  matchedLinkedPayeeId: string | null = null;
  matchedLinkedPayeeNameHighlight: string | null = null;
  linkedPayees$: Observable<Payee[]> = of([]);
  parentPayee$: Observable<Payee | null> = of(null);

  get totalPublishingBalance(): number {
    if (this.version() === 'admin') {
      return this.payee?.internalProperties?.totalBalance || 0;
    }

    return !!this.composer?.totalStatementSum ? this.composer.totalStatementSum : this.payee?.internalProperties?.internalStatementsSumIndividual || 0;
  }

  get composers(): any[] {
    return this.payee?._highlightResult?.internalProperties?.composers || this.payee?.internalProperties?.composers;
  }

  constructor() {
    addIcons({personCircle,chevronDown,returnDownForwardOutline,checkmarkCircleOutline,timeOutline,closeCircleOutline,banOutline});
    effect(() => {
      this.artist = this.artistInput() as any;
      this.payee = this.payeeInput() as any;
      this.composer = this.composerInput() as any;
      const ids = this.payee?.internalProperties?.linkedPayees || [];
      this.linkedPayees$ = (ids && ids.length > 0) ? this.db.getPayees(ids) : of([]);

      const parentId = this.payee?.internalProperties?.parentPayee;
      this.parentPayee$ = parentId ? this.db.getPayee(parentId) : of(null);

      // Auto expand if parent was shown due to a matching child in search results
      const matchedId = (this.payee as any)?.__matchedLinkedPayeeId as string | undefined;
      const highlight = (this.payee as any)?.__matchedLinkedPayeeNameHighlight as string | undefined;
      this.autoExpandLinkedPayees = !!matchedId;
      this.matchedLinkedPayeeId = matchedId || null;
      this.matchedLinkedPayeeNameHighlight = highlight || null;
    });
  }

  ngOnInit() {
    // no-op; reactive sync is set up in the constructor to ensure injection context
  }
  ngOnDestroy(): void {
  }

  onChangeClick() {
    this.changeClick.emit();
  }

  get isIndividualBeneficiary(): boolean {
    return !this.payee?.internalProperties?.parentPayee &&
      (!this.payee?.internalProperties?.linkedPayees?.length ||
        this.payee?.internalProperties?.linkedPayees?.length === 0);
  }

  get hasLinkedPayees(): boolean {
    return this.payee?.internalProperties?.linkedPayees?.length > 0;
  }

  get isChildPayee(): boolean {
    return !!this.payee?.internalProperties?.parentPayee;
  }

  getContractName(clickId: string, contractTypes: ContractType[]): string {
    const contractType = contractTypes.find(ct => ct.id === clickId);
    return contractType ? contractType.name : '(Externo)';
  }

  preventBubble(event: Event) {
    if (!(event.target instanceof HTMLSpanElement)) {
      event.stopPropagation();
    }
  }

  onLinkedPayeeClick(id: string, event: Event) {
    try { event.stopPropagation(); } catch { }
    if (id) {
      this.linkedPayeeSelected.emit(id);
    }
  }
}
